import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Heading,
  Text,
  useColorModeValue,
  VStack,
  HStack,
  Icon,
  Badge,
  IconButton,
  Flex,
  Image,
  Skeleton,
  keyframes,
} from '@chakra-ui/react';
import { useCustomToast } from '../utils/customToast';
import {
  FaStar,
  FaChevronRight,
  FaHeart,
  FaPlus,
  FaThumbsUp,
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import { logger } from '../utils/logger';
import { getBusinessImages } from '../services/imageService';
import { getYearData } from '../services/yearService';
import { getHistoricalTraffic, getReviews } from '../services/reviewService';
import { LineChart, Line, ResponsiveContainer } from 'recharts';
import {
  hasCompetitiveIntel,
  formatNumber,
  formatTrafficDisplay,
  calculateSeoScoreGrowth,
  getSeoScoreColor,
  estimateSeoScore,
} from '../utils/competitiveIntelHelpers';

// Pulse animation for the illumination effect
const pulseGlow = keyframes`
  0% { box-shadow: 0 0 5px rgba(237, 137, 54, 0.3), 0 0 10px rgba(237, 137, 54, 0.2); }
  50% { box-shadow: 0 0 15px rgba(237, 137, 54, 0.5), 0 0 25px rgba(237, 137, 54, 0.3); }
  100% { box-shadow: 0 0 5px rgba(237, 137, 54, 0.3), 0 0 10px rgba(237, 137, 54, 0.2); }
`;

// Thumbs up confetti float animation
const thumbsUpFloat = keyframes`
  0% {
    transform: translateY(0) rotate(0deg) scale(1);
    opacity: 1;
  }
  20% {
    transform: translateY(-40px) rotate(-20deg) scale(1.2);
    opacity: 1;
  }
  40% {
    transform: translateY(-80px) rotate(15deg) scale(1.1);
    opacity: 0.9;
  }
  60% {
    transform: translateY(-120px) rotate(-10deg) scale(1);
    opacity: 0.7;
  }
  80% {
    transform: translateY(-160px) rotate(20deg) scale(0.9);
    opacity: 0.4;
  }
  100% {
    transform: translateY(-200px) rotate(-15deg) scale(0.7);
    opacity: 0;
  }
`;

// Celebration overlay fade in/out
const celebrationFade = keyframes`
  0% { opacity: 0; }
  8% { opacity: 1; }
  92% { opacity: 1; }
  100% { opacity: 0; }
`;

// "Great Choice!" text bounce animation
const textBounce = keyframes`
  0% { transform: scale(0.5); opacity: 0; }
  40% { transform: scale(1.15); opacity: 1; }
  60% { transform: scale(0.95); }
  80% { transform: scale(1.05); }
  100% { transform: scale(1); opacity: 1; }
`;

// Individual ThumbsUp confetti particle
const ThumbsUpParticle = ({ delay, startX, startY, size, rotation }) => {
  return (
    <Box
      position="absolute"
      left={`${startX}%`}
      top={`${startY}%`}
      animation={`${thumbsUpFloat} 4s ease-out ${delay}s forwards`}
      zIndex={10}
    >
      <Icon 
        as={FaThumbsUp} 
        color="green.400" 
        boxSize={size}
        transform={`rotate(${rotation}deg)`}
        filter="drop-shadow(0 2px 4px rgba(72, 187, 120, 0.4))"
      />
    </Box>
  );
};

// Celebration overlay component with confetti and message
const CelebrationOverlay = ({ isVisible }) => {
  // Generate random particles with staggered delays for longer effect
  const particles = useMemo(() => {
    const count = 25;
    return Array.from({ length: count }, (_, i) => ({
      id: i,
      delay: Math.random() * 1.5, // Stagger over 1.5 seconds
      startX: 5 + Math.random() * 90, // Random X position (5-95%)
      startY: 40 + Math.random() * 50, // Random Y position (40-90%)
      size: 4 + Math.random() * 4, // Random size (4-8)
      rotation: -30 + Math.random() * 60, // Random rotation (-30 to 30)
    }));
  }, []);

  if (!isVisible) return null;

  return (
    <Box
      position="absolute"
      top={0}
      left={0}
      right={0}
      bottom={0}
      bg="rgba(255, 255, 255, 0.95)"
      zIndex={100}
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      animation={`${celebrationFade} 5s ease-in-out forwards`}
      overflow="hidden"
      borderRadius="lg"
    >
      {/* Thumbs up confetti particles */}
      {particles.map((particle) => (
        <ThumbsUpParticle
          key={particle.id}
          delay={particle.delay}
          startX={particle.startX}
          startY={particle.startY}
          size={particle.size}
          rotation={particle.rotation}
        />
      ))}
      
      {/* "Great Choice!" message */}
      <VStack spacing={3} zIndex={20}>
        <Icon 
          as={FaThumbsUp} 
          color="green.500" 
          boxSize={16}
          animation={`${textBounce} 0.8s ease-out forwards`}
        />
        <Text
          fontSize="3xl"
          fontWeight="bold"
          color="green.600"
          animation={`${textBounce} 0.8s ease-out 0.2s forwards`}
          opacity={0}
          textAlign="center"
        >
          Great Choice!
        </Text>
        <Text
          fontSize="sm"
          color="gray.600"
          animation={`${textBounce} 0.8s ease-out 0.5s forwards`}
          opacity={0}
          textAlign="center"
          px={4}
        >
          Your favorite will now be ⭐ starred on all dashboards
        </Text>
      </VStack>
    </Box>
  );
};

// Helper to calculate 3-month growth
const calculateThreeMonthGrowth = (monthlyReviewCounts = {}, months = []) => {
  if (months.length < 3) return 0;
  const lastThreeMonths = months.slice(-3);
  return lastThreeMonths.reduce((sum, month) => sum + (monthlyReviewCounts[month] || 0), 0);
};

// Helper function to get business initials
const getBusinessInitials = (name) => {
  if (!name) return '?';
  const words = name.trim().split(/\s+/).filter(word => word.length > 0);
  if (words.length === 0) return '?';
  if (words.length === 1) {
    return words[0].substring(0, 2).toUpperCase();
  }
  const skipWords = ['and', 'the', 'a', 'an', 'of', 'in', 'for', '&'];
  const significantWords = words.filter(w => !skipWords.includes(w.toLowerCase()));
  if (significantWords.length >= 2) {
    return (significantWords[0][0] + significantWords[1][0]).toUpperCase();
  }
  if (significantWords.length === 1) {
    return significantWords[0].substring(0, 2).toUpperCase();
  }
  return (words[0][0] + (words[1] ? words[1][0] : '')).toUpperCase();
};

// Selectable business item for empty state
const SelectableBusinessItem = ({
  business,
  imageUrl,
  onSelect,
  borderColor,
  hoverBorderColor,
  statBg,
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const placeholderBg = useColorModeValue('gray.100', 'gray.600');

  return (
    <Box
      p={2}
      borderWidth="2px"
      borderColor={borderColor}
      borderRadius="md"
      cursor="pointer"
      transition="all 0.2s"
      _hover={{
        borderColor: hoverBorderColor,
        bg: statBg,
        transform: 'translateY(-1px)',
        shadow: 'md',
      }}
      onClick={() => onSelect(business)}
    >
      <HStack spacing={3}>
        {/* Business Image/Initials */}
        <Box
          position="relative"
          w="40px"
          h="40px"
          borderRadius="md"
          overflow="hidden"
          flexShrink={0}
          bg={placeholderBg}
        >
          {imageUrl && !imageError ? (
            <>
              {!imageLoaded && (
                <Skeleton
                  position="absolute"
                  top={0}
                  left={0}
                  w="100%"
                  h="100%"
                  startColor="gray.100"
                  endColor="gray.300"
                />
              )}
              <Image
                src={imageUrl}
                alt={business.name}
                w="100%"
                h="100%"
                objectFit="cover"
                loading="lazy"
                onLoad={() => setImageLoaded(true)}
                onError={() => setImageError(true)}
                opacity={imageLoaded ? 1 : 0}
                transition="opacity 0.3s ease-in-out"
              />
            </>
          ) : (
            <Flex
              w="100%"
              h="100%"
              justify="center"
              align="center"
              bg="white"
              borderWidth="1px"
              borderColor={borderColor}
            >
              <Text
                fontSize="sm"
                fontWeight="bold"
                color="gray.400"
                letterSpacing="wider"
              >
                {getBusinessInitials(business.name)}
              </Text>
            </Flex>
          )}
        </Box>

        {/* Business Info */}
        <VStack align="flex-start" spacing={0} flex={1} minW={0}>
          <Text fontWeight="semibold" fontSize="sm" noOfLines={1}>
            {business.name}
          </Text>
          <HStack spacing={2}>
            <Text fontSize="xs" color="gray.500">
              ⭐ {parseInt(business.rating_count || business.total_reviews || 0).toLocaleString()}
            </Text>
            <Text fontSize="xs" color="gray.500">
              🏆 {business.rating_avg?.toFixed(1) || 'N/A'}
            </Text>
          </HStack>
        </VStack>

        {/* Add button indicator */}
        <Flex
          w="24px"
          h="24px"
          borderRadius="full"
          bg="orange.100"
          justify="center"
          align="center"
          flexShrink={0}
        >
          <Icon as={FaPlus} boxSize={2.5} color="orange.500" />
        </Flex>
      </HStack>
    </Box>
  );
};

// Full Horizontal Business Insights Card component
const FavoriteBusinessItem = ({ 
  business, 
  imageUrl, 
  borderColor, 
  hoverBg, 
  onBusinessClick, 
  onToggleFavorite,
  yearData,
  trafficData,
  statBg,
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const placeholderBg = useColorModeValue('gray.100', 'gray.600');

  // Find year data for this business
  const businessYearData = useMemo(() => {
    if (!yearData || !business.maps_uri) return null;
    return yearData.find(yd => yd.maps_uri === business.maps_uri);
  }, [yearData, business.maps_uri]);

  // Calculate growth metrics
  const metrics = useMemo(() => {
    const monthlyReviewCounts = businessYearData?.data?.monthlyReviewCounts || {};
    const months = Object.keys(monthlyReviewCounts).sort();
    const growth = calculateThreeMonthGrowth(monthlyReviewCounts, months);
    
    // Sentiment calculation
    const sentimentData = businessYearData?.data?.sentimentBreakdown;
    let sentimentScore = null;
    let sentimentEmoji = '😐';
    if (sentimentData) {
      const total = sentimentData.positive + sentimentData.neutral + sentimentData.negative;
      if (total > 0) {
        sentimentScore = Math.round((sentimentData.positive / total) * 100);
        if (sentimentScore >= 80) sentimentEmoji = '😊';
        else if (sentimentScore >= 60) sentimentEmoji = '🙂';
        else if (sentimentScore >= 40) sentimentEmoji = '😐';
        else sentimentEmoji = '😕';
      }
    }

    return {
      growth,
      sentimentScore,
      sentimentEmoji,
    };
  }, [businessYearData]);

  // Competitive intel data
  const hasCompIntel = hasCompetitiveIntel(business);
  // Calculate SEO Score and growth (same calculation as WebTrafficDashboard)
  const seoScoreData = useMemo(() => {
    return calculateSeoScoreGrowth(business, trafficData);
  }, [business, trafficData]);
  
  // Build traffic history map for the helper function
  const trafficHistoryMap = useMemo(() => {
    if (!trafficData || !business?.maps_uri) return null;
    return { [business.maps_uri]: trafficData };
  }, [trafficData, business?.maps_uri]);

  // Process cumulative reviews data for chart
  const cumulativeReviewsData = useMemo(() => {
    if (!businessYearData?.data?.monthlyReviewCounts) return [];
    
    const sortedData = Object.entries(businessYearData.data.monthlyReviewCounts)
      .map(([month, count]) => ({
        date: new Date(month + '-01T00:00:00').getTime(),
        count: count,
        originalMonth: month
      }))
      .sort((a, b) => a.date - b.date)
      .slice(-12);
    
    const historicalTotal = sortedData.reduce((sum, item) => sum + (item.count || 0), 0);
    const actualTotal = business.rating_count || business.total_reviews || historicalTotal;
    const baselineReviews = Math.max(0, actualTotal - historicalTotal);
    
    let cumulative = baselineReviews;
    return sortedData.map(item => {
      cumulative += item.count || 0;
      return { value: cumulative };
    });
  }, [businessYearData, business.rating_count, business.total_reviews]);

  return (
    <Box
      p={3}
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="md"
      cursor="pointer"
      transition="all 0.2s"
      _hover={{ bg: hoverBg, transform: 'translateY(-1px)', shadow: 'sm' }}
      onClick={() => onBusinessClick(business)}
    >
      {/* Main horizontal layout - all content in one row */}
      <Flex gap={3} align="stretch" w="100%">
        
        {/* Section 1: Image */}
        <Box
          position="relative"
          w="100px"
          h="100px"
          borderRadius="md"
          overflow="hidden"
          flexShrink={0}
          bg={placeholderBg}
        >
          {imageUrl && !imageError ? (
            <>
              {!imageLoaded && (
                <Skeleton
                  position="absolute"
                  top={0}
                  left={0}
                  w="100%"
                  h="100%"
                  startColor="gray.100"
                  endColor="gray.300"
                />
              )}
              <Image
                src={imageUrl}
                alt={business.name}
                w="100%"
                h="100%"
                objectFit="cover"
                loading="lazy"
                onLoad={() => setImageLoaded(true)}
                onError={() => setImageError(true)}
                opacity={imageLoaded ? 1 : 0}
                transition="opacity 0.3s ease-in-out"
              />
            </>
          ) : (
            <Flex
              w="100%"
              h="100%"
              justify="center"
              align="center"
              bg="white"
              borderWidth="1px"
              borderColor={borderColor}
            >
              <Text
                fontSize="2xl"
                fontWeight="bold"
                color="gray.400"
                letterSpacing="wider"
              >
                {getBusinessInitials(business.name)}
              </Text>
            </Flex>
          )}
        </Box>

        {/* Section 2: Name and 6 metrics */}
        <VStack align="flex-start" spacing={0.5} flex="1" minW={0}>
          {/* Name and badge row */}
          <HStack spacing={1} w="100%">
            <Text fontWeight="bold" fontSize="1.5rem" noOfLines={1} flex={1}>
              {business.name}
            </Text>
            {business.rating_avg >= 4.5 && (
              <Badge colorScheme="green" fontSize="2xs" flexShrink={0}>
                Top Rated
              </Badge>
            )}
          </HStack>

          {/* 6 Metrics in 3x2 grid */}
          <Box 
            display="grid" 
            gridTemplateColumns="repeat(3, 1fr)" 
            gridTemplateRows="repeat(2, 1fr)"
            gap={0.5} 
            w="100%"
            flex="1"
          >
            <HStack spacing={0.5} px={1} py={0.5} bg={statBg} borderRadius="sm" justify="center">
              <Text fontSize="2xs">⭐</Text>
              <Text fontSize="2xs" fontWeight="bold">{parseInt(business.rating_count || business.total_reviews || 0).toLocaleString()}</Text>
            </HStack>
            
            <HStack spacing={0.5} px={1} py={0.5} bg={statBg} borderRadius="sm" justify="center">
              <Text fontSize="2xs">🏆</Text>
              <Text fontSize="2xs" fontWeight="bold">{business.rating_avg?.toFixed(1) || 'N/A'}</Text>
            </HStack>
            
            <HStack spacing={0.5} px={1} py={0.5} bg={statBg} borderRadius="sm" justify="center">
              <Text fontSize="2xs">📈</Text>
              <Text fontSize="2xs" fontWeight="bold" color={metrics.growth > 0 ? "green.600" : "gray.600"}>
                {metrics.growth > 0 ? `+${metrics.growth}` : '0'}
              </Text>
            </HStack>
            
            <HStack spacing={0.5} px={1} py={0.5} bg={statBg} borderRadius="sm" justify="center">
              <Text fontSize="2xs">{metrics.sentimentEmoji}</Text>
              <Text fontSize="2xs" fontWeight="bold">{metrics.sentimentScore !== null ? `${metrics.sentimentScore}%` : 'N/A'}</Text>
            </HStack>
            
            <HStack spacing={0.5} px={1} py={0.5} bg={statBg} borderRadius="sm" justify="center">
              <Text fontSize="2xs">🎯</Text>
              <Text fontSize="2xs" fontWeight="bold" color={getSeoScoreColor(seoScoreData.currentScore > 0 ? seoScoreData.currentScore : estimateSeoScore(business))}>
                {seoScoreData.currentScore > 0
                  ? seoScoreData.currentScore.toFixed(1)
                  : `~${estimateSeoScore(business).toFixed(1)}`}
              </Text>
            </HStack>
            
            <HStack spacing={0.5} px={1} py={0.5} bg={statBg} borderRadius="sm" justify="center">
              <Text fontSize="2xs">🌐</Text>
              <Text fontSize="2xs" fontWeight="bold">
                {formatTrafficDisplay(business, trafficHistoryMap, false)}
              </Text>
            </HStack>
          </Box>
        </VStack>

        {/* Section 3: Charts stacked */}
        <VStack spacing={0.5} flex="1" minW={0}>
          {/* Reviews trend chart */}
          <Box w="100%" flex="1">
            <Text fontSize="2xs" color="gray.500">📊 Reviews Trend</Text>
            <Box h="28px" bg={statBg} borderRadius="sm" p={0.5}>
              {cumulativeReviewsData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={cumulativeReviewsData}>
                    <Line 
                      type="monotone" 
                      dataKey="value" 
                      stroke="#48BB78" 
                      strokeWidth={1.5}
                      dot={false} 
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <Flex h="100%" align="center" justify="center">
                  <Text fontSize="2xs" color="gray.400">No data</Text>
                </Flex>
              )}
            </Box>
          </Box>
          
          {/* Traffic trend chart */}
          <Box w="100%" flex="1">
            <Text fontSize="2xs" color="gray.500">🌐 Traffic Trend</Text>
            <Box h="28px" bg={statBg} borderRadius="sm" p={0.5}>
              {trafficData && trafficData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trafficData}>
                    <Line 
                      type="monotone" 
                      dataKey="organicClicks" 
                      stroke="#3182CE" 
                      strokeWidth={1.5}
                      dot={false} 
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <Flex h="100%" align="center" justify="center">
                  <Text fontSize="2xs" color="gray.400">No data</Text>
                </Flex>
              )}
            </Box>
          </Box>
        </VStack>

        {/* Section 5: Favorite button */}
        <Flex align="center" flexShrink={0}>
          <IconButton
            icon={<Icon as={FaStar} />}
            aria-label="Remove from favorites"
            size="sm"
            colorScheme="yellow"
            variant="ghost"
            onClick={(e) => onToggleFavorite(business.maps_uri, business.name, e)}
          />
        </Flex>
      </Flex>
    </Box>
  );
};

const FavoritesWidget = () => {
  const navigate = useNavigate();
  const { favoriteBusinesses, toggleFavorite } = useUser();
  const [businessImages, setBusinessImages] = useState({});
  const [yearData, setYearData] = useState([]);
  const [trafficHistory, setTrafficHistory] = useState({});
  const [allBusinesses, setAllBusinesses] = useState([]);
  const [loadingBusinesses, setLoadingBusinesses] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const toast = useCustomToast();

  // Fetch business images (async, non-blocking)
  useEffect(() => {
    let mounted = true;
    
    const fetchImages = async () => {
      try {
        const images = await getBusinessImages();
        if (mounted && images) {
          const imageMap = {};
          images.forEach(img => {
            if (img.maps_uri && img.high_res_image_url_fetched) {
              imageMap[img.maps_uri] = img.high_res_image_url_fetched;
            }
          });
          setBusinessImages(imageMap);
        }
      } catch (error) {
        logger.error('Error fetching business images:', error);
        // Don't block UI on image fetch failure
      }
    };

    fetchImages();
    
    return () => {
      mounted = false;
    };
  }, []);

  // Fetch year data for growth metrics
  useEffect(() => {
    let mounted = true;
    
    const fetchYearData = async () => {
      try {
        const data = await getYearData();
        if (mounted && data) {
          setYearData(data);
        }
      } catch (error) {
        logger.error('Error fetching year data:', error);
      }
    };

    fetchYearData();
    
    return () => {
      mounted = false;
    };
  }, []);

  // Fetch historical traffic data
  useEffect(() => {
    let mounted = true;
    
    const fetchTrafficData = async () => {
      try {
        const result = await getHistoricalTraffic();
        if (mounted && result?.businesses) {
          const trafficMap = {};
          result.businesses.forEach(business => {
            if (business.mapsUri && business.timeSeries) {
              const chartData = business.timeSeries
                .sort((a, b) => a.yearMonth.localeCompare(b.yearMonth))
                .slice(-12)
                .map(point => ({
                  month: new Date(point.yearMonth + '-01T00:00:00').toLocaleDateString('en-US', {
                    month: 'short',
                    year: '2-digit'
                  }),
                  organicClicks: point.organicClicks || 0
                }));
              trafficMap[business.mapsUri] = chartData;
            }
          });
          setTrafficHistory(trafficMap);
        }
      } catch (error) {
        logger.error('Error fetching traffic history:', error);
      }
    };

    fetchTrafficData();
    
    return () => {
      mounted = false;
    };
  }, []);

  // Fetch all businesses when there are no favorites (for selection)
  useEffect(() => {
    let mounted = true;
    
    const fetchAllBusinesses = async () => {
      // Only fetch if no favorites
      if (favoriteBusinesses && favoriteBusinesses.length > 0) {
        return;
      }
      
      try {
        setLoadingBusinesses(true);
        const data = await getReviews();
        if (mounted && data) {
          // Sort by rating_count descending for better UX
          const sortedBusinesses = Array.isArray(data) 
            ? [...data].sort((a, b) => 
                (b.rating_count || b.total_reviews || 0) - (a.rating_count || a.total_reviews || 0)
              )
            : [];
          setAllBusinesses(sortedBusinesses);
        }
      } catch (error) {
        logger.error('Error fetching all businesses:', error);
      } finally {
        if (mounted) {
          setLoadingBusinesses(false);
        }
      }
    };

    fetchAllBusinesses();
    
    return () => {
      mounted = false;
    };
  }, [favoriteBusinesses]);

  // Color mode values
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const headerBg = useColorModeValue('yellow.50', 'yellow.900');
  const accentColor = useColorModeValue('yellow.500', 'yellow.400');
  const statBg = useColorModeValue('gray.50', 'gray.700');
  const glowBorderColor = useColorModeValue('orange.300', 'orange.400');
  const promptBg = useColorModeValue('orange.50', 'orange.900');

  // Handle toggling favorite (from widget, we can only remove since it's already the favorite)
  const handleToggleFavorite = async (mapsUri, businessName, e) => {
    e.stopPropagation(); // Prevent card click
    try {
      const result = await toggleFavorite(mapsUri);
      if (!result.isNowFavorited) {
        toast({
          title: 'Favorite removed',
          description: `${businessName} is no longer your favorite`,
          status: 'info',
          duration: 2000,
          isClosable: true,
        });
      }
    } catch (error) {
      logger.error('Error toggling favorite:', error);
      toast({
        title: 'Error',
        description: 'Failed to update favorite',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleViewAll = () => {
    navigate('/dashboards/favorites');
  };

  const handleBusinessClick = (business) => {
    // Navigate to the favorites dashboard when clicking a favorite business
    navigate('/dashboards/favorites');
  };

  const handleSelectFavorite = async (business) => {
    try {
      // Show celebration animation first
      setShowCelebration(true);
      
      // Toggle favorite after a short delay so animation is visible
      await toggleFavorite(business.maps_uri);
      
      // Hide celebration after animation completes (5 seconds)
      setTimeout(() => {
        setShowCelebration(false);
      }, 5000);
      
      toast({
        title: 'Great Choice! 🎉',
        description: 'Your favorite will now be ⭐ starred on all dashboards',
        status: 'success',
        duration: 3500,
        isClosable: true,
      });
    } catch (error) {
      setShowCelebration(false);
      logger.error('Error setting favorite:', error);
      toast({
        title: 'Error',
        description: 'Failed to set favorite',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Empty state - show all businesses for selection
  if (!favoriteBusinesses || favoriteBusinesses.length === 0) {
    return (
      <Box
        bg={cardBg}
        borderWidth="2px"
        borderColor={glowBorderColor}
        borderRadius="lg"
        overflow="hidden"
        h="340px"
        display="flex"
        flexDirection="column"
        animation={`${pulseGlow} 2s ease-in-out infinite`}
        position="relative"
      >
        {/* Celebration overlay with thumbs up confetti */}
        <CelebrationOverlay isVisible={showCelebration} />
        
        {/* Header with prompt */}
        <Box bg={promptBg} px={4} py={3} borderBottomWidth="1px" borderColor={glowBorderColor} flexShrink={0}>
          <Flex align="center" justify="space-between">
            <HStack>
              <Icon as={FaStar} color="orange.500" boxSize={4} />
              <Heading size="md" textTransform="uppercase" fontSize="1em">My Favorite</Heading>
            </HStack>
            <HStack spacing={2}>
              <Icon as={FaHeart} color="orange.400" boxSize={3} />
              <Text fontSize="xs" fontWeight="medium" color="orange.600">
                Add a favorite business to see insights!
              </Text>
            </HStack>
          </Flex>
        </Box>

        {/* Scrollable business list for selection */}
        <Box flex="1" overflowY="auto" p={3}>
          {loadingBusinesses ? (
            <VStack spacing={2} align="stretch">
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} height="56px" borderRadius="md" />
              ))}
            </VStack>
          ) : allBusinesses.length > 0 ? (
            <VStack spacing={2} align="stretch">
              {allBusinesses.map((business) => (
                <SelectableBusinessItem
                  key={business.maps_uri || business.name}
                  business={business}
                  imageUrl={businessImages[business.maps_uri]}
                  onSelect={handleSelectFavorite}
                  borderColor={borderColor}
                  hoverBorderColor={glowBorderColor}
                  statBg={statBg}
                />
              ))}
            </VStack>
          ) : (
            <VStack spacing={3} py={6} textAlign="center" justify="center" h="100%">
              <Icon as={FaHeart} boxSize={10} color="gray.400" />
              <Text color="gray.500" fontSize="sm">
                No businesses available
              </Text>
            </VStack>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      bg={cardBg}
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="lg"
      overflow="hidden"
      h="340px"
      display="flex"
      flexDirection="column"
      position="relative"
    >
      {/* Celebration overlay - also shown in normal state to persist through transition */}
      <CelebrationOverlay isVisible={showCelebration} />
      
      {/* Header */}
      <Box bg={headerBg} px={4} py={3} borderBottomWidth="1px" borderColor={borderColor} flexShrink={0}>
        <Flex align="center" justify="space-between">
          <HStack>
            <Icon as={FaStar} color={accentColor} boxSize={4} />
            <Heading size="md" textTransform="uppercase" fontSize="1em">My Favorite</Heading>
          </HStack>
          <Button
            size="xs"
            variant="ghost"
            colorScheme="yellow"
            rightIcon={<Icon as={FaChevronRight} />}
            onClick={handleViewAll}
          >
            View Details
          </Button>
        </Flex>
      </Box>

      {/* Single favorite business display - users can only have one favorite */}
      <Box flex="1" overflowY="auto" p={3}>
        <VStack spacing={2} align="stretch">
          {/* Only display the first favorite (safeguard for single-favorite constraint) */}
          {favoriteBusinesses.slice(0, 1).map((business) => (
            <FavoriteBusinessItem
              key={business.maps_uri}
              business={business}
              imageUrl={businessImages[business.maps_uri]}
              borderColor={borderColor}
              hoverBg={hoverBg}
              onBusinessClick={handleBusinessClick}
              onToggleFavorite={handleToggleFavorite}
              yearData={yearData}
              trafficData={trafficHistory[business.maps_uri]}
              statBg={statBg}
            />
          ))}
        </VStack>
      </Box>
    </Box>
  );
};

export default FavoritesWidget;
